import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useToast } from '@/components/Toast'
import { useAuth } from '@/hooks/useAuth'
import { useDepartments } from '@/hooks/useOrg'
import type { UserRole } from '@/lib/api'

export const STAFF_ONLY_NOTICE = 'That page is for HRAD staff'

interface Props {
  children: ReactNode
  requireRole?: UserRole | UserRole[]
  /** Also admit users who head at least one department (needs the department list). */
  allowDeptHead?: boolean
}

function Spinner({ fullScreen = true }: { fullScreen?: boolean }) {
  return (
    <div className={`flex items-center justify-center ${fullScreen ? 'min-h-screen' : 'min-h-[400px]'}`}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-wegen-600 border-t-transparent" />
    </div>
  )
}

function RedirectWithNotice() {
  const toast = useToast()
  useEffect(() => {
    toast.info(STAFF_ONLY_NOTICE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <Navigate to="/dashboard" replace />
}

function DeptHeadGate({ userId, children }: { userId: number; children: ReactNode }) {
  const { data, isLoading } = useDepartments()
  if (isLoading) return <Spinner fullScreen={false} />
  if ((data ?? []).some((d) => d.head_user_id === userId)) return <>{children}</>
  return <RedirectWithNotice />
}

export function ProtectedRoute({ children, requireRole, allowDeptHead }: Props) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) return <Spinner />

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole]
    if (!allowed.includes(user.role)) {
      if (allowDeptHead) return <DeptHeadGate userId={user.id}>{children}</DeptHeadGate>
      return <RedirectWithNotice />
    }
  }

  return <>{children}</>
}
